// Progress for a long AI call, read from the server instead of guessed.
//
// The pattern: mint an id, send it as `run_id` on the POST, and poll
// GET /api/runs/:id while that POST is still open. The bar then moves when the
// server reaches a milestone, not when a timer fires, so a stalled call stops
// moving and a fast one finishes early.
//
// Replaces two habits: a setInterval creeping to 90% with invented phase names,
// and three fixed step labels advancing every 700ms whatever the server did.
import { API_BASE } from './api.js';

/** A run id the server will accept (see RUN_ID_RE: 8-64 of [A-Za-z0-9_-]). */
export function newRunId() {
  const rand = () => Math.random().toString(36).slice(2, 10);
  return `${rand()}${rand()}`.slice(0, 32);
}

/**
 * Poll a run until it stops changing or `stop()` is called.
 * `onTick(run)` receives { status, step, steps, label, note, found, error, pct }.
 * Returns a stop function; always call it when the request settles.
 */
export function pollRun(id, onTick, { intervalMs = 700 } = {}) {
  let stopped = false;
  let timer = null;

  const tick = async () => {
    if (stopped) return;
    try {
      const res = await fetch(`${API_BASE}/runs/${id}`, { credentials: 'include' });
      if (res.ok) {
        const run = await res.json();
        // Percent is derived from real milestones. Step 0 means "accepted, not
        // started", and a completed run is 100 even if a step was skipped.
        const pct = run.status === 'completed' ? 100
          : Math.min(95, Math.round((run.step / Math.max(1, run.steps)) * 100));
        if (!stopped) onTick({ ...run, pct });
      }
      // A 404 is normal: the POST may not have opened the run yet, or it aged
      // out. Keep polling; the caller's own request is the source of truth.
    } catch { /* a dropped poll must never fail the actual request */ }
    if (!stopped) timer = setTimeout(tick, intervalMs);
  };

  timer = setTimeout(tick, 150);   // first poll fast, so the panel fills quickly
  return () => { stopped = true; clearTimeout(timer); };
}
